import { apiClient } from './api';

export type StudentAssessmentAlertLevel = 'red' | 'yellow' | 'green' | 'gray';
export type StudentAssessmentScheduleInterval = 'hourly' | 'daily' | 'weekly' | 'monthly';
export type StudentAssessmentRunStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'skipped';
export type StudentAssessmentSelectionScope = 'group' | 'class' | 'students';

export type StudentAssessmentActivationConflictResolution = 'move_to_new' | 'keep_previous';

/** Mindestens ein Schüler hat noch Aktivierungsläufe unter deinem Konto (409). */
export class ActivateStudentJobConflictError extends Error {
  readonly conflicts: StudentAssessmentActivateConflictStudent[];

  constructor(conflicts: StudentAssessmentActivateConflictStudent[]) {
    super('STUDENT_JOB_CONFLICT');
    this.name = 'ActivateStudentJobConflictError';
    this.conflicts = conflicts;
  }
}

export interface StudentAssessmentActivateConflictStudent {
  studentUnid: string;
  className: string | null;
  studentDisplayName: string;
  existingBatchIds: string[];
  legacyActivationRunIds: number[];
}

export interface StudentAssessmentAutomationConfig {
  id: number;
  userId: number;
  className: string;
  enabled: boolean;
  scheduleInterval: StudentAssessmentScheduleInterval;
  scheduleTimeLocal: string | null;
  timezone: string;
  triggerOnSchedule: boolean;
  triggerOnEvent: boolean;
  eventCooldownMinutes: number;
  notifyTeacher: boolean;
  notifyParents: boolean;
  notifyStudents: boolean;
  minAlertLevel: StudentAssessmentAlertLevel;
  includeCompetencies: boolean;
  includeTeacherDistribution: boolean;
  maxStudentsPerRun: number;
  lastScheduledRunAt: string | null;
  lastEventRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StudentAssessmentRun {
  id: number;
  jobId: string | null;
  source: string;
  triggerType: string | null;
  userId: number | null;
  className: string | null;
  studentUnid: string;
  studentFirstName: string | null;
  studentLastName: string | null;
  studentDisplayName: string;
  initiatedByUserId: number | null;
  initiatedByDisplayName: string | null;
  status: StudentAssessmentRunStatus;
  alertLevel: StudentAssessmentAlertLevel | null;
  mainIssue: string | null;
  dataQualityIndex: number | null;
  runMetadataJson: unknown;
  resultMemo: string | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface StudentAssessmentActivationDetails {
  scheduleInterval?: 'daily' | 'weekly' | 'monthly';
  triggerOnSchedule?: boolean;
  triggerOnEvent?: boolean;
  eventCooldownMinutes?: number;
  notifyTeacher?: boolean;
  notifyParents?: boolean;
  notifyStudents?: boolean;
  minAlertLevel?: StudentAssessmentAlertLevel;
  lookbackDays?: number;
}

export interface StudentAssessmentActivatePayload {
  scope: StudentAssessmentSelectionScope;
  className?: string;
  groupId?: number;
  studentUnids?: string[];
  details?: StudentAssessmentActivationDetails;
  /** Nach 409-Konflikt: pro konfliktbehaftetem UNID Auflösung setzen */
  studentConflictResolutions?: Record<string, StudentAssessmentActivationConflictResolution>;
}

export interface StudentAssessmentActivationJob {
  runId: number;
  jobId: string;
  studentUnid: string;
}

export interface StudentAssessmentActivateResponse {
  scope: StudentAssessmentSelectionScope;
  selectedStudents: number;
  activatedStudents: number;
  queuedJobs: number;
  skipped: Array<{ studentUnid: string; reason: string }>;
  jobs: StudentAssessmentActivationJob[];
}

export interface StudentAssessmentRunPauseResponse {
  runId: number;
  paused: boolean;
}

export interface StudentAssessmentRunStopResponse {
  runId: number;
  stopped: boolean;
  cancelResult: unknown;
}

export interface StudentAssessmentBatchActionResponse {
  batchId: string;
  runs?: number;
  pausedCount?: number;
  stoppedCount?: number;
  deletedRuns?: number;
}

export interface StudentAssessmentStudentRunResponse {
  jobId: string;
  runId: number;
}

export interface StudentAssessmentClassJob {
  jobId: string;
  runId: number;
  studentUnid: string;
}

export interface StudentAssessmentClassRunResponse {
  className: string;
  queuedStudents: number;
  jobs: StudentAssessmentClassJob[];
}

export interface StudentAssessmentConfigPayload {
  enabled?: boolean;
  scheduleInterval?: StudentAssessmentScheduleInterval;
  scheduleTimeLocal?: string | null;
  timezone?: string;
  triggerOnSchedule?: boolean;
  triggerOnEvent?: boolean;
  eventCooldownMinutes?: number;
  notifyTeacher?: boolean;
  notifyParents?: boolean;
  notifyStudents?: boolean;
  minAlertLevel?: StudentAssessmentAlertLevel;
  includeCompetencies?: boolean;
  includeTeacherDistribution?: boolean;
  maxStudentsPerRun?: number;
}

type ApiResponse<T> = { success: boolean; data: T; error?: string };

function asBoolean(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

function normalizeConfig(raw: any): StudentAssessmentAutomationConfig {
  return {
    id: Number(raw?.id || 0),
    userId: Number(raw?.user_id || raw?.userId || 0),
    className: String(raw?.class_name || raw?.className || ''),
    enabled: asBoolean(raw?.enabled),
    scheduleInterval: String(raw?.schedule_interval || raw?.scheduleInterval || 'weekly') as StudentAssessmentScheduleInterval,
    scheduleTimeLocal: raw?.schedule_time_local || raw?.scheduleTimeLocal || null,
    timezone: String(raw?.timezone || 'Europe/Berlin'),
    triggerOnSchedule: asBoolean(raw?.trigger_on_schedule ?? raw?.triggerOnSchedule),
    triggerOnEvent: asBoolean(raw?.trigger_on_event ?? raw?.triggerOnEvent),
    eventCooldownMinutes: Number(raw?.event_cooldown_minutes ?? raw?.eventCooldownMinutes ?? 240),
    notifyTeacher: asBoolean(raw?.notify_teacher ?? raw?.notifyTeacher),
    notifyParents: asBoolean(raw?.notify_parents ?? raw?.notifyParents),
    notifyStudents: asBoolean(raw?.notify_students ?? raw?.notifyStudents),
    minAlertLevel: String(raw?.min_alert_level || raw?.minAlertLevel || 'yellow') as StudentAssessmentAlertLevel,
    includeCompetencies: asBoolean(raw?.include_competencies ?? raw?.includeCompetencies),
    includeTeacherDistribution: asBoolean(raw?.include_teacher_distribution ?? raw?.includeTeacherDistribution),
    maxStudentsPerRun: Number(raw?.max_students_per_run ?? raw?.maxStudentsPerRun ?? 35),
    lastScheduledRunAt: raw?.last_scheduled_run_at || raw?.lastScheduledRunAt || null,
    lastEventRunAt: raw?.last_event_run_at || raw?.lastEventRunAt || null,
    createdAt: String(raw?.created_at || raw?.createdAt || ''),
    updatedAt: String(raw?.updated_at || raw?.updatedAt || ''),
  };
}

function parseJsonField(value: unknown): unknown {
  if (value == null || typeof value !== 'string') return value ?? null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeRun(raw: any): StudentAssessmentRun {
  const studentFirstName = raw?.student_first_name || raw?.studentFirstName || null;
  const studentLastName = raw?.student_last_name || raw?.studentLastName || null;
  const studentDisplayName = [studentLastName, studentFirstName].filter(Boolean).join(', ').trim();
  return {
    id: Number(raw?.id || 0),
    jobId: raw?.job_id || raw?.jobId || null,
    source: String(raw?.source || 'manual'),
    triggerType: raw?.trigger_type || raw?.triggerType || null,
    userId: raw?.user_id != null ? Number(raw.user_id) : null,
    className: raw?.class_name || raw?.className || null,
    studentUnid: String(raw?.student_unid || raw?.studentUnid || ''),
    studentFirstName,
    studentLastName,
    studentDisplayName,
    initiatedByUserId: raw?.user_id != null ? Number(raw.user_id) : null,
    initiatedByDisplayName: raw?.initiated_by_username || null,
    status: String(raw?.status || 'queued') as StudentAssessmentRunStatus,
    alertLevel: (raw?.alert_level || raw?.alertLevel || null) as StudentAssessmentAlertLevel | null,
    mainIssue: raw?.main_issue || raw?.mainIssue || null,
    dataQualityIndex: raw?.data_quality_index != null ? Number(raw.data_quality_index) : null,
    runMetadataJson: parseJsonField(raw?.run_metadata_json ?? raw?.runMetadataJson),
    resultMemo: raw?.result_memo || raw?.resultMemo || null,
    errorMessage: raw?.error_message || raw?.errorMessage || null,
    createdAt: String(raw?.created_at || raw?.createdAt || ''),
    startedAt: raw?.started_at || raw?.startedAt || null,
    completedAt: raw?.completed_at || raw?.completedAt || null,
  };
}

export const studentAssessmentsApi = {
  async getAutomationConfigs(): Promise<StudentAssessmentAutomationConfig[]> {
    const response = await apiClient.get('/student-assessments/automation-configs');
    const payload = response.data as ApiResponse<any[]>;
    return (payload.data || []).map((item) => normalizeConfig(item));
  },

  async saveAutomationConfig(
    className: string,
    body: StudentAssessmentConfigPayload
  ): Promise<StudentAssessmentAutomationConfig> {
    const response = await apiClient.put(`/student-assessments/automation-configs/${encodeURIComponent(className)}`, body);
    const payload = response.data as ApiResponse<any>;
    return normalizeConfig(payload.data);
  },

  async runStudent(unid: string, lookbackDays = 60): Promise<StudentAssessmentStudentRunResponse> {
    const response = await apiClient.post(`/student-assessments/run/student/${encodeURIComponent(unid)}`, {
      lookbackDays,
    });
    const payload = response.data as ApiResponse<StudentAssessmentStudentRunResponse>;
    return payload.data;
  },

  async runClass(
    className: string,
    lookbackDays = 60,
    maxStudents = 35
  ): Promise<StudentAssessmentClassRunResponse> {
    const response = await apiClient.post(`/student-assessments/run/class/${encodeURIComponent(className)}`, {
      lookbackDays,
      maxStudents,
    });
    const payload = response.data as ApiResponse<StudentAssessmentClassRunResponse>;
    return payload.data;
  },

  async getRuns(limit = 50): Promise<StudentAssessmentRun[]> {
    const response = await apiClient.get('/student-assessments/runs', { params: { limit } });
    const payload = response.data as ApiResponse<any[]>;
    return (payload.data || []).map((item) => normalizeRun(item));
  },

  async activate(payloadInput: StudentAssessmentActivatePayload): Promise<StudentAssessmentActivateResponse> {
    try {
      const response = await apiClient.post('/student-assessments/activate', payloadInput);
      const payload = response.data as ApiResponse<StudentAssessmentActivateResponse>;
      return payload.data;
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: Record<string, unknown> } };
      const data = err.response?.data;
      if (
        err.response?.status === 409 &&
        data?.code === 'STUDENT_JOB_CONFLICT' &&
        Array.isArray(data.conflicts)
      ) {
        throw new ActivateStudentJobConflictError(
          data.conflicts as StudentAssessmentActivateConflictStudent[]
        );
      }
      throw error;
    }
  },

  async pauseRun(runId: number): Promise<StudentAssessmentRunPauseResponse> {
    const response = await apiClient.post(`/student-assessments/runs/${runId}/pause`);
    const payload = response.data as ApiResponse<StudentAssessmentRunPauseResponse>;
    return payload.data;
  },

  async stopRun(runId: number): Promise<StudentAssessmentRunStopResponse> {
    const response = await apiClient.post(`/student-assessments/runs/${runId}/stop`);
    const payload = response.data as ApiResponse<StudentAssessmentRunStopResponse>;
    return payload.data;
  },

  async deleteRun(runId: number): Promise<{ runId: number; deleted: boolean }> {
    const response = await apiClient.delete(`/student-assessments/runs/${runId}`);
    const payload = response.data as ApiResponse<{ runId: number; deleted: boolean }>;
    return payload.data;
  },

  async pauseBatch(batchId: string): Promise<StudentAssessmentBatchActionResponse> {
    const response = await apiClient.post(`/student-assessments/runs/batch/${encodeURIComponent(batchId)}/pause`);
    const payload = response.data as ApiResponse<StudentAssessmentBatchActionResponse>;
    return payload.data;
  },

  async stopBatch(batchId: string): Promise<StudentAssessmentBatchActionResponse> {
    const response = await apiClient.post(`/student-assessments/runs/batch/${encodeURIComponent(batchId)}/stop`);
    const payload = response.data as ApiResponse<StudentAssessmentBatchActionResponse>;
    return payload.data;
  },

  async deleteBatch(batchId: string): Promise<StudentAssessmentBatchActionResponse> {
    const response = await apiClient.delete(`/student-assessments/runs/batch/${encodeURIComponent(batchId)}`);
    const payload = response.data as ApiResponse<StudentAssessmentBatchActionResponse>;
    return payload.data;
  },
};
